/*
 * Draggable, resizable pop-up, written by hand because react-draggable-resizable-modal calls ReactDOM.findDOMNode
 * (removed in React 19). Drag and resize come from react-draggable with `nodeRef`, which needs no findDOMNode.
 * It keeps the `flexible-modal`, `body` and `close-icon` class names, so the overrides in custom.css and
 * responsive.css (fixed position, mobile width) still apply.
 */
import React, { useEffect, useRef, useState } from "react";
import Draggable, { DraggableCore } from "react-draggable";
import styled from "styled-components";
import Swal from "sweetalert2";
import { tokens } from "../../public/tokens";

const { colors, globals } = tokens;

const MIN_WIDTH = 200;
const MIN_BODY_HEIGHT = 100;

const StyledMask = styled.div({
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 2,
    background: "rgba(55, 55, 55, 0.6)",
});

/**
 * `$isMoving` turns off iframe pointer events while dragging or resizing, since the video would swallow the mouse.
 */
const StyledBox = styled.div(({ $isMoving }) => ({
    position: "fixed",
    zIndex: 99,
    border: `1px solid ${colors.neutral.grey["400"]}`,
    background: colors.neutral.white,

    "& iframe": {
        pointerEvents: $isMoving ? "none" : "auto",
    },
}));

const StyledBody = styled.div({
    padding: `0 ${globals["spacing-3"]}`,
    overflowY: "auto",
});

const StyledDragArea = styled.div({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "60px",
    cursor: "move",
});

const StyledResizer = styled.div({
    position: "absolute",
    right: 0,
    bottom: 0,
    width: globals["spacing-2-5"],
    height: globals["spacing-2-5"],
    margin: "5px",
    borderRight: `2px solid ${colors.neutral.grey["800"]}`,
    borderBottom: `2px solid ${colors.neutral.grey["800"]}`,
    cursor: "se-resize",
});

const StyledCloseIcon = styled.button({
    position: "absolute",
    top: globals["spacing-2-5"],
    right: globals["spacing-2-5"],
    width: "25px",
    height: "25px",
    padding: 0,
    border: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
});

/**
 * Pop-up that can be dragged by its top 60px and resized from its bottom-right corner; used for the course feed video.
 * Renders in place (no portal) because responsive.css positions it through its `.modal-update` ancestor.
 * The × and a click on the mask call `onRequestClose`, and so does Escape unless a Swal is open or a control already
 * handled it.
 * @param {Object} props `isOpen`, `onRequestClose`, `children`, plus `initWidth` (box width, px) and
 * `initHeight` (content height, px), the starting size before the user resizes it.
 * @example
 * <DraggableModal isOpen={open} onRequestClose={close} initWidth={640} initHeight={340}>
 *     <iframe src={videoUrl} />
 * </DraggableModal>
 */
function DraggableModal({ isOpen, ...props }) {
    if (!isOpen) {
        return null;
    }

    return (
        <OpenDraggableModal {...props} />
    );
}

/**
 * The mounted part of DraggableModal, split out so its state and effects reset each time it opens.
 * @param {Object} props DraggableModal's props without `isOpen`.
 */
function OpenDraggableModal({ onRequestClose, initWidth = 640, initHeight = 340, children }) {
    const boxRef = useRef(null);
    const resizerRef = useRef(null);
    const onRequestCloseRef = useRef(onRequestClose);
    const [size, setSize] = useState({ width: initWidth, height: initHeight });
    const [isMoving, setIsMoving] = useState(false);
    const [position] = useState(() => ({ top: 100, left: window.innerWidth / 2 - initWidth / 2 }));
    onRequestCloseRef.current = onRequestClose;

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape" && !event.defaultPrevented && !Swal.isVisible() && onRequestCloseRef.current) {
                onRequestCloseRef.current();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    const handleResize = (event, { deltaX, deltaY }) => {
        setSize((previousSize) => ({
            width: Math.max(MIN_WIDTH, previousSize.width + deltaX),
            height: Math.max(MIN_BODY_HEIGHT, previousSize.height + deltaY),
        }));
    };

    return (
        <>
            <StyledMask className="flexible-modal-mask" onClick={onRequestClose} />
            <Draggable
                nodeRef={boxRef}
                handle=".flexible-modal-drag-area"
                onStart={() => setIsMoving(true)}
                onStop={() => setIsMoving(false)}
            >
                <StyledBox
                    ref={boxRef}
                    className="flexible-modal"
                    role="dialog"
                    aria-modal="true"
                    $isMoving={isMoving}
                    style={{ width: size.width, top: position.top, left: position.left }}
                >
                    <StyledBody className="body" style={{ height: size.height }}>
                        {children}
                    </StyledBody>
                    <StyledDragArea className="flexible-modal-drag-area" />
                    <DraggableCore
                        nodeRef={resizerRef}
                        onStart={() => setIsMoving(true)}
                        onDrag={handleResize}
                        onStop={() => setIsMoving(false)}
                    >
                        <StyledResizer ref={resizerRef} className="flexible-modal-resizer" />
                    </DraggableCore>
                    <StyledCloseIcon type="button" className="close-icon" aria-label="Close" onClick={onRequestClose}>
                        <i className="fas fa-times" aria-hidden="true" />
                    </StyledCloseIcon>
                </StyledBox>
            </Draggable>
        </>
    );
}

export default DraggableModal;
